import {
  backendBaseUrl,
  backendFetch,
  backendRequest,
  clearBackendSession,
  getBackendCurrentUser,
  getBackendUserById,
  getBackendUsersByIds,
  getStoredAccessToken,
  signInBackendUser,
} from "@/lib/backend/api";

const C = {
  users: "users",
  posts: "posts",
  follows: "follows",
  messages: "messages",
  notifications: "notifications",
  deals: "deals",
  escrows: "escrows",
  liveStreams: "live-streams",
  liveProducts: "live-products",
  liveMessages: "live-stream-chat",
  livePurchases: "live-purchases",
  scheduledLives: "scheduled-lives",
  trustScores: "trust-scores",
  trustEvents: "trust-events",
  ratings: "ratings",
  referralCodes: "referral-codes",
  transactions: "transactions",
  violations: "violations",
  kycRecords: "kyc-records",
  kycSessions: "kyc-sessions",
  userGuides: "user-guides",
  guideCategories: "user-guide-categories",
  videos: "videos",
  videoStats: "video-stats",
  videoInteractions: "video-interactions",
  videoTips: "video-tips",
  tipPayouts: "tip-payouts",
  videoReports: "video-reports",
  postComments: "post-comments",
} as const;

export const appwriteConfig = {
  url: backendBaseUrl,
  projectId: "busiconn-backend",
  databaseId: "busiconn-db",
  storageId: "busiconn-media",
  userCollectionId: C.users,
  postCollectionId: C.posts,
  savesCollectionId: "post-saves",
  messagesCollectionId: C.messages,
  notificationsCollectionId: C.notifications,
  escrowCollectionId: C.escrows,
  dealStage: C.deals,
  products: C.liveProducts,
  liveStreams: C.liveStreams,
  liveStreamsChat: C.liveMessages,
  streamPurchases: C.livePurchases,
  followsCollectionId: C.follows,
  scheduledLivesCollectionId: C.scheduledLives,
  userViolationsCollectionId: C.violations,
  trustScoreCollectionId: C.trustScores,
  trustScoreEventsCollectionId: C.trustEvents,
  ratingsCollectionId: C.ratings,
  referralCodesCollectionId: C.referralCodes,
  transactionsCollectionId: C.transactions,
  cashfreeCreateOrder: "cashfree-create-order",
  cashfreeVerifyPayment: "cashfree-verify-payment",
  cashfreeReleasePayment: "cashfree-release-payment",
  createBeneficiaryFunctionId: "kyc-create-beneficiary",
  generateAgoraTokenFunctionId: "live-generate-token",
  realtimeUpdates: false,
  createKYCSessionFunctionId: "kyc-create-session",
  verifyKYCFunctionId: "kyc-verify",
  checkExistingUser: "auth-check-existing-user",
  checkKYCStatusFunctionId: "kyc-check-status",
  trackViewerFunctionId: "live-track-viewer",
  kycRecordsCollectionId: C.kycRecords,
  kycSessionsCollectionId: C.kycSessions,
  userGuideArtice: C.userGuides,
  userGuideCategories: C.guideCategories,
  videosCollectionId: C.videos,
  videoStatsCollectionId: C.videoStats,
  videoInteractionsCollectionId: C.videoInteractions,
  videoTipsCollectionId: C.videoTips,
  tipPayoutsCollectionId: C.tipPayouts,
  videoReportsCollectionId: C.videoReports,
  postComments: C.postComments,
  videoBucketId: "video-bucket",
};

type Doc = Record<string, any>;
const fileUrlCache = new Map<string, string>();
const now = () => new Date().toISOString();
const id = () => globalThis.crypto?.randomUUID?.() ?? `id_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
const mediaItems = (value: any) => Array.isArray(value) ? value.filter(Boolean) : [];
export const isValidMediaUrl = (value: unknown): value is string => {
  if (typeof value !== "string") return false;
  const url = value.trim().toLowerCase();
  return url.length > 0 && url !== "[]" && url !== "{}" && url !== "null" && url !== "undefined";
};
const toDoc = (value: any): any => {
  if (!value) return value;
  const v: Doc = Array.isArray(value) ? value.map(toDoc) : { ...value };
  if (!Array.isArray(v)) {
    v.$id ??= v.id ?? id();
    v.id ??= v.$id;
    v.$createdAt ??= v.createdAtUtc ?? v.createdAt ?? v.timestampUtc ?? now();
    v.$updatedAt ??= v.updatedAtUtc ?? v.updatedAt ?? now();
    v.createdAt ??= v.createdAtUtc ?? v.$createdAt;
    v.updatedAt ??= v.updatedAtUtc ?? v.$updatedAt;
    v.deliveredAt ??= v.deliveredAtUtc;
    v.cancellationRequestedAt ??= v.cancellationRequestedAtUtc;
    v.cancellationResponseDueAt ??= v.cancellationResponseDueAtUtc;
    v.cancellationRespondedAt ??= v.cancellationRespondedAtUtc;
    v.created_at ??= v.createdAt ?? v.createdAtUtc ?? v.$createdAt;
    v.updated_at ??= v.updatedAt ?? v.updatedAtUtc ?? v.$updatedAt;
    if (v.senderId !== undefined && v.sender_id === undefined) v.sender_id = v.senderId;
    if (v.recipientId !== undefined && v.recipient_id === undefined) v.recipient_id = v.recipientId;
    if (v.messageText !== undefined && v.message === undefined) v.message = v.messageText;
    if (v.isRead !== undefined && v.is_read === undefined) v.is_read = v.isRead;
    if (v.attachmentIds !== undefined && v.attachment_ids === undefined) v.attachment_ids = v.attachmentIds;
    if (v.attachmentMetadataJson !== undefined && v.attachment_metadata_json === undefined) v.attachment_metadata_json = v.attachmentMetadataJson;
    if (v.isAgentMessage !== undefined && v.is_agent_message === undefined) v.is_agent_message = v.isAgentMessage;
    if (v.content !== undefined && v.text === undefined) v.text = v.content;
    if (v.timestampUtc !== undefined && v.timestamp === undefined) v.timestamp = v.timestampUtc;
    if (v.createdAtUtc !== undefined && v.timestamp === undefined) v.timestamp = v.createdAtUtc;
    if (v.startTimeUtc !== undefined && v.startTime === undefined) v.startTime = v.startTimeUtc;
    if (v.endTimeUtc !== undefined && v.endTime === undefined) v.endTime = v.endTimeUtc;
    if (v.title !== undefined && v.name === undefined) v.name = v.title;
    if (v.email !== undefined && v.emailOrPhone === undefined) v.emailOrPhone = v.email;
    if (v.acceptedSla !== undefined && v.acceptedSLA === undefined) v.acceptedSLA = v.acceptedSla;
    if (typeof v.dataJson === "string") {
      try {
        const data = JSON.parse(v.dataJson);
        if (data && typeof data === "object") {
          Object.entries(data).forEach(([key, entry]) => {
            if (v[key] === undefined) v[key] = entry;
          });
        }
      } catch {
        // Keep malformed legacy notification metadata isolated from rendering.
      }
    }
    if (v.disputeRaisedAtUtc !== undefined && v.disputeRaisedAt === undefined) v.disputeRaisedAt = v.disputeRaisedAtUtc;
    if (v.media && !v.Media) v.Media = v.media;
    if (v.Media && !v.media) v.media = v.Media;
    const hasMediaArray = Array.isArray(v.media) || Array.isArray(v.Media);
    const media = mediaItems(v.media ?? v.Media)
      .filter((item) => isValidMediaUrl(item?.url))
      .map((item) => ({ ...item, url: item.url.trim() }))
      .sort((left, right) => (left?.sortOrder ?? 0) - (right?.sortOrder ?? 0));
    if (hasMediaArray) {
      v.media = media;
      v.Media = media;
      if (v.imageUrl === undefined) v.imageUrl = media.map((item) => item.url);
      if (v.imageId === undefined) v.imageId = media.map((item) => item.fileId);
      if (v.mediaType === undefined) v.mediaType = media.map((item) => item.mediaType);
    }
    if (v.likesCount !== undefined && v.likes === undefined) v.likes = v.likesCount;
    if (v.savesCount !== undefined && v.saves === undefined) v.saves = v.savesCount;
    // Chat components still use Appwrite's document type guard.
    if (v.sender_id !== undefined || v.senderId !== undefined) {
      v.$databaseId ??= appwriteConfig.databaseId;
      v.$collectionId ??= appwriteConfig.messagesCollectionId;
    }
  }
  return v;
};
const toList = (documents: any[], total?: number) => ({ documents: documents.map(toDoc), total: total ?? documents.length });
const listFromBody = (body: any) => {
  const documents = Array.isArray(body) ? body : body?.documents ?? [];
  return toList(documents, Array.isArray(body) ? body.length : body?.total ?? documents.length);
};
const currentUserId = async () => { try { const u = await getBackendCurrentUser(); return String(u?.id ?? u?.$id ?? ""); } catch { return ""; } };
const qs = (queries?: string[]) => JSON.stringify(queries ?? []);
const parseQuery = (query: string) => {
  try {
    return JSON.parse(query) as { method?: string; attribute?: string; values?: unknown[] } | null;
  } catch {
    return null;
  }
};
const firstId = (queries?: string[]) => {
  for (const query of queries ?? []) {
    const parsed = parseQuery(query);
    const parsedId = Array.isArray(parsed?.values)
      ? parsed.values.find((value) => typeof value === "string" && /[0-9a-fA-F-]{36}/.test(value))
      : null;
    if (typeof parsedId === "string") return parsedId;
  }
  return qs(queries).match(/([0-9a-fA-F-]{36})/)?.[1] ?? "";
};
const firstWord = (queries?: string[]) => {
  for (const query of queries ?? []) {
    const parsed = parseQuery(query);
    const parsedWord = Array.isArray(parsed?.values)
      ? parsed.values.find((value) => typeof value === "string" && value.trim().length >= 3)
      : null;
    if (typeof parsedWord === "string") return parsedWord;
  }
  return qs(queries).match(/"([^"]{3,})"/)?.[1] ?? "";
};
const queryValues = (queries: string[] | undefined, field: string) => {
  const escaped = field.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parsedValues = (queries ?? []).flatMap((query) => {
    const parsed = parseQuery(query);
    if (parsed?.attribute === field && Array.isArray(parsed.values)) {
      return parsed.values.map((value) => String(value)).filter(Boolean);
    }
    return [];
  });

  if (parsedValues.length > 0) {
    return Array.from(new Set(parsedValues));
  }

  return Array.from(new Set((queries ?? []).flatMap(query => {
    const match = query.match(new RegExp(`["']${escaped}["']\\s*,\\s*\\[?([^\\)]*)`, "i"));
    return Array.from(match?.[1]?.matchAll(/["']([^"']+)["']/g) ?? []).map(value => value[1]);
  })));
};
const queryValue = (queries: string[] | undefined, field: string) => queryValues(queries, field)[0] ?? "";
const hasQueryValue = (queries: string[] | undefined, field: string, expected: string) =>
  queryValues(queries, field).some((value) => value.toLowerCase() === expected.toLowerCase());
const limit = (queries?: string[], fallback = 100) => {
  for (const query of queries ?? []) {
    const parsed = parseQuery(query);
    if (parsed?.method === "limit" && Array.isArray(parsed.values) && parsed.values[0] !== undefined) {
      return Number(parsed.values[0]);
    }
  }
  return Number(qs(queries).match(/limit\((\d+)\)/i)?.[1] ?? fallback);
};
const offset = (queries?: string[]) => {
  for (const query of queries ?? []) {
    const parsed = parseQuery(query);
    if (parsed?.method === "offset" && Array.isArray(parsed.values) && parsed.values[0] !== undefined) {
      return Number(parsed.values[0]);
    }
  }
  return Number(qs(queries).match(/offset\((\d+)\)/i)?.[1] ?? 0);
};
const hasPublishedTrue = (queries?: string[]) => (queries ?? []).some((query) => {
  const parsed = parseQuery(query);
  return parsed?.attribute === "published" && Array.isArray(parsed.values) && String(parsed.values[0]).toLowerCase() === "true";
}) || /published[^a-zA-Z0-9_]+true/i.test(qs(queries));
const tagsCsv = (tags: any) => Array.isArray(tags) ? tags.join(",") : tags ?? null;
const numberOrDefault = (value: any, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
const authEmail = (value: string) => value.includes("@") ? value.trim().toLowerCase() : `${value.startsWith("+91") ? value : `+91${value}`}@busiconn.com`;
const normalizeMediaUrl = (value: string) => {
  if (!isValidMediaUrl(value)) return "";
  const trimmedValue = value.trim();
  if (trimmedValue.startsWith("http://") || trimmedValue.startsWith("https://")) return trimmedValue;
  if (trimmedValue.startsWith("/api/v1/media/") || trimmedValue.startsWith("/uploads/")) {
    return `${backendBaseUrl}${trimmedValue}`;
  }
  return `${backendBaseUrl}/api/v1/media/${encodeURIComponent(trimmedValue)}`;
};
const isMediaKey = (key?: string) => !!key && /(url|uri|thumbnail|image|video|avatar|preview|download|file)$/i.test(key);
const normalizeBackendResponse = <T>(value: T, key?: string): T => {
  if (Array.isArray(value)) {
    return value.map((entry) => normalizeBackendResponse(entry, key)) as T;
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([entryKey, entry]) => [entryKey, normalizeBackendResponse(entry, entryKey)])) as T;
  }

  if (typeof value === "string") {
    if (value.startsWith("/api/v1/media/") || value.startsWith("/uploads/")) {
      return normalizeMediaUrl(value) as T;
    }

    return (isMediaKey(key) ? normalizeMediaUrl(value) : value) as T;
  }

  return value;
};

async function exec(path: string, init: RequestInit = {}) {
  const res = await backendFetch(path, init);

  const responseBody = res.status === 204
    ? ""
    : (res.headers.get("content-type") ?? "").includes("application/json")
      ? JSON.stringify(normalizeBackendResponse(await res.json()))
      : await res.text();

  return { responseStatusCode: res.status, responseBody };
}

async function fn(functionId: string, body?: any) {
  const payload = typeof body === "string" ? (() => { try { return JSON.parse(body); } catch { return body; } })() : (body ?? {});
  switch (functionId) {
    case appwriteConfig.cashfreeCreateOrder: return exec("/api/v1/payments/create-order", { method: "POST", body: JSON.stringify(payload) });
    case appwriteConfig.cashfreeVerifyPayment:
      return payload?.paymentId || payload?.payment_id || payload?.order_id
        ? exec("/api/v1/payments/transactions/verify", { method: "POST", body: JSON.stringify({ orderId: payload.orderId ?? payload.order_id, paymentId: payload.paymentId ?? payload.payment_id, paymentMethod: payload.paymentMethod ?? payload.payment_method ?? null, metadataJson: payload.metadataJson ?? payload.metadata_json ?? null }) })
        : exec(`/api/v1/payments/transactions/${encodeURIComponent(payload.orderId ?? payload.order_id ?? "")}`, { method: "GET" });
    case appwriteConfig.cashfreeReleasePayment: {
      if (!payload?.dealId) {
        return exec("/api/v1/escrows/auto-release", { method: "POST" });
      }

      // The ASP.NET endpoint owns the deterministic transfer ID so retrying a
      // completion request cannot create another Cashfree payout for the deal.
      const result = await exec(`/api/v1/deals/${encodeURIComponent(payload.dealId)}/complete`, { method: "POST" });
      if (result.responseStatusCode < 200 || result.responseStatusCode >= 300) {
        return result;
      }

      // Keep the response contract used by the former Appwrite function while
      // the backend endpoint continues returning the updated deal resource.
      const backendResult = JSON.parse(result.responseBody || "{}");
      if (backendResult.success === true) {
        return result;
      }

      const deal = backendResult;
      return {
        ...result,
        responseBody: JSON.stringify({
          success: true,
          message: "Payment initiated successfully",
          data: {
            transfer_id: deal.sellerPayoutId ?? deal.seller_payout_id ?? null,
            amount: payload.sellerAmount ?? null,
            status: "PENDING",
          },
          deal,
        }),
      };
    }
    case appwriteConfig.createBeneficiaryFunctionId: return exec("/api/v1/kyc/beneficiary", { method: "POST", body: JSON.stringify(payload) });
    case appwriteConfig.verifyKYCFunctionId: return payload?.action === "verify_bank"
      ? exec("/api/v1/kyc/verify-bank", { method: "POST", body: JSON.stringify(payload) })
      : exec("/api/v1/kyc/verify-pan", { method: "POST", body: JSON.stringify(payload) });
    case appwriteConfig.checkKYCStatusFunctionId: return exec(payload?.userId ? `/api/v1/kyc/${encodeURIComponent(payload.userId)}/status` : "/api/v1/kyc/me/status", { method: "GET" });
    case appwriteConfig.createKYCSessionFunctionId:
      return exec("/api/v1/kyc/sessions", { method: "POST", body: JSON.stringify({ userId: payload?.userId ?? payload?.user_id ?? await currentUserId(), verificationType: payload?.verificationType ?? payload?.verification_type ?? payload?.type ?? "kyc" }) });
    case appwriteConfig.trackViewerFunctionId:
      switch (payload?.action) {
        case "join": return exec("/api/v1/live/viewer-join", { method: "POST", body: JSON.stringify(payload) });
        case "heartbeat": return exec("/api/v1/live/viewer-heartbeat", { method: "POST", body: JSON.stringify(payload) });
        case "leave": return exec("/api/v1/live/viewer-leave", { method: "POST", body: JSON.stringify(payload) });
        case "get_status": return exec(`/api/v1/live/viewer-status/${encodeURIComponent(payload.streamId)}`, { method: "GET" });
        case "cleanup": return exec("/api/v1/live/viewer-cleanup", { method: "POST", body: JSON.stringify(payload) });
        default: return exec(`/api/v1/live/analytics/${encodeURIComponent(payload.streamId ?? "")}`, { method: "GET" });
      }
    case appwriteConfig.generateAgoraTokenFunctionId:
      return exec("/api/v1/live/token", { method: "POST", body: JSON.stringify({ channel: payload?.channel ?? payload?.streamId ?? "", userId: payload?.userId ?? payload?.uid ?? await currentUserId(), role: payload?.role ?? "viewer" }) });
    case appwriteConfig.checkExistingUser:
      return exec(`/api/v1/users/exists?emailOrPhone=${encodeURIComponent(payload?.emailOrPhone ?? payload?.phone ?? payload?.email ?? "")}`, { method: "GET" });
    default: return exec("/health/check", { method: "GET" });
  }
}

async function listDocuments(collectionId: string, queries?: string[]) {
  const q = qs(queries);
  const l = limit(queries);
  const o = offset(queries);
  const guessedId = firstId(queries);
  const guessedText = firstWord(queries);

  switch (collectionId) {
    case appwriteConfig.postCollectionId: {
      if (q.includes("likedBy")) {
        const res = await exec(`/api/v1/posts/${await currentUserId()}/liked`);
        return listFromBody(JSON.parse(res.responseBody || "[]"));
      }
      if (q.includes("savedBy")) {
        const res = await exec(`/api/v1/posts/${await currentUserId()}/saved`);
        return listFromBody(JSON.parse(res.responseBody || "[]"));
      }
      const params = new URLSearchParams({ limit: String(l), offset: String(o) });
      if (hasQueryValue(queries, "isActivated", "false")) {
        const res = await exec(`/api/v1/admin/posts/pending?${params.toString()}`);
        return listFromBody(JSON.parse(res.responseBody || "{}"));
      }
      if (q.includes("creatorId") || q.includes("userId")) params.set("creatorId", guessedId || guessedText);
      if (q.includes("search") || q.includes("caption") || q.includes("tags") || q.includes("query")) params.set("query", guessedText || guessedId);
      const res = await exec(`/api/v1/posts?${params.toString()}`);
      const body = JSON.parse(res.responseBody || "{}");
      return listFromBody(body);
    }
    case appwriteConfig.userCollectionId: {
      if (q.includes("by-ids")) {
        const users = await getBackendUsersByIds(Array.from(new Set((q.match(/([0-9a-fA-F-]{36})/g) ?? []))));
        return toList(users, users.length);
      }
      if (guessedId) return toList([await getBackendUserById(guessedId)], 1);
      try { const res = await exec(`/api/v1/users?limit=${l}`); const body = JSON.parse(res.responseBody || "{}"); return toList(body.documents ?? [], body.total ?? 0); } catch { return toList([]); }
    }
    case appwriteConfig.messagesCollectionId: {
      const senderId = queryValue(queries, "sender_id") || queryValue(queries, "senderId");
      const recipientId = queryValue(queries, "recipient_id") || queryValue(queries, "recipientId");
      const dealId = queryValue(queries, "dealId");
      const isRead = queryValue(queries, "is_read") || queryValue(queries, "isRead");
      const params = new URLSearchParams({ limit: String(l) });
      if (senderId) params.set("senderId", senderId);
      if (recipientId) params.set("recipientId", recipientId);
      if (dealId) params.set("dealId", dealId);
      if (isRead) params.set("isRead", isRead);

      // A chat view always needs both directions. Keep dealId on the conversation
      // endpoint instead of falling back to the one-direction metadata query.
      if (senderId && recipientId && !isRead) {
        const conversationParams = new URLSearchParams({
          senderId,
          recipientId,
          limit: String(l),
        });
        if (dealId) conversationParams.set("dealId", dealId);
        const res = await exec(`/api/v1/messages/conversation?${conversationParams.toString()}`);
        return listFromBody(JSON.parse(res.responseBody || "{}"));
      }

      // Metadata requests need all filters preserved for unread counts and
      // deal-wide last-message lookups.
      if (dealId || isRead) {
        const res = await exec(`/api/v1/messages?${params.toString()}`);
        return listFromBody(JSON.parse(res.responseBody || "{}"));
      }
      if (q.includes("conversation")) {
        const ids = Array.from(new Set((q.match(/([0-9a-fA-F-]{36})/g) ?? [])));
        const res = await exec(`/api/v1/messages/conversation?senderId=${encodeURIComponent(ids[0] ?? guessedId)}&recipientId=${encodeURIComponent(ids[1] ?? guessedText)}&currentUserId=${encodeURIComponent(await currentUserId())}&limit=${l}`);
        const body = JSON.parse(res.responseBody || "{}");
        return listFromBody(body);
      }
      if (q.includes("last")) {
        const res = await exec(`/api/v1/messages/users/${encodeURIComponent(guessedId || guessedText)}/last`);
        const body = JSON.parse(res.responseBody || "[]");
        return listFromBody(body);
      }
      if (guessedId || guessedText) {
        const res = await exec(`/api/v1/messages/users/${encodeURIComponent(guessedId || guessedText)}?limit=${l}`);
        const body = JSON.parse(res.responseBody || "{}");
        return listFromBody(body);
      }
      return toList([]);
    }
    case appwriteConfig.notificationsCollectionId: { const res = await exec("/api/v1/notifications/me"); const body = JSON.parse(res.responseBody || "[]"); return listFromBody(body); }
    case appwriteConfig.followsCollectionId: {
      if (q.includes("followingId")) { const res = await exec(`/api/v1/users/${encodeURIComponent(guessedId)}/followers`); const body = JSON.parse(res.responseBody || "{}"); return toList(body.documents ?? body ?? [], body.total ?? body.documents?.length ?? 0); }
      if (q.includes("followerId")) { const res = await exec(`/api/v1/users/${encodeURIComponent(guessedId)}/following`); const body = JSON.parse(res.responseBody || "{}"); return toList(body.documents ?? body ?? [], body.total ?? body.documents?.length ?? 0); }
      return toList([]);
    }
    case appwriteConfig.dealStage: {
      const buyerId = queryValue(queries, "buyerId");
      const sellerId = queryValue(queries, "sellerId");
      const postId = queryValue(queries, "postId");
      if (buyerId && sellerId) {
        const includeCompleted = !q.includes("notEqual");
        const params = new URLSearchParams({ buyerId, sellerId, includeCompleted: String(includeCompleted) });
        if (postId) params.set("postId", postId);
        const res = await exec(`/api/v1/deals/lookup?${params.toString()}`);
        const body = JSON.parse(res.responseBody || "null");
        return toList(body ? [body] : [], body ? 1 : 0);
      }
      const res = await exec("/api/v1/deals?limit=200");
      const body = JSON.parse(res.responseBody || "{}");
      return listFromBody(body);
    }
    case appwriteConfig.escrowCollectionId: { const res = await exec("/api/v1/escrows?limit=200"); const body = JSON.parse(res.responseBody || "{}"); return listFromBody(body); }
    case appwriteConfig.products: {
      const params = new URLSearchParams({ limit: String(l) });
      if (hasQueryValue(queries, "isActivated", "false")) {
        const res = await exec(`/api/v1/admin/live-products/pending?${params.toString()}`);
        return listFromBody(JSON.parse(res.responseBody || "{}"));
      }
      if (q.includes("sellerId") && (guessedId || guessedText)) params.set("sellerId", guessedId || guessedText);
      const res = await exec(`/api/v1/live/products?${params.toString()}`);
      const body = JSON.parse(res.responseBody || "{}");
      return listFromBody(body);
    }
    case appwriteConfig.liveStreams: { const res = await exec("/api/v1/live/streams?limit=200"); const body = JSON.parse(res.responseBody || "{}"); return listFromBody(body); }
    case appwriteConfig.liveStreamsChat: { const res = await exec(`/api/v1/live/messages/${encodeURIComponent(guessedId)}`); const body = JSON.parse(res.responseBody || "{}"); return listFromBody(body); }
    case appwriteConfig.streamPurchases: { const res = await exec(`/api/v1/live/purchases/${encodeURIComponent(guessedId)}`); const body = JSON.parse(res.responseBody || "{}"); return listFromBody(body); }
    case appwriteConfig.scheduledLivesCollectionId: {
      const res = hasQueryValue(queries, "isActivated", "false")
        ? await exec(`/api/v1/admin/scheduled-lives/pending?limit=${l}`)
        : await exec("/api/v1/live/scheduled");
      return listFromBody(JSON.parse(res.responseBody || "{}"));
    }
    case appwriteConfig.postComments: {
      const params = new URLSearchParams({ limit: String(l), offset: String(o) });
      if (q.includes("postId") && (guessedId || guessedText)) params.set("postId", guessedId || guessedText);
      const res = await exec(`/api/v1/post-comments?${params.toString()}`);
      const body = JSON.parse(res.responseBody || "{}");
      return listFromBody(body);
    }
    case appwriteConfig.userGuideArtice: {
      const params = new URLSearchParams({ limit: String(l), offset: String(o) });
      if (q.includes("published")) params.set("published", String(hasPublishedTrue(queries)));
      if (q.includes("categoryId") && (guessedId || guessedText)) params.set("categoryId", guessedId || guessedText);
      const res = await exec(`/api/v1/user-guides?${params.toString()}`);
      const body = JSON.parse(res.responseBody || "{}");
      return listFromBody(body);
    }
    case appwriteConfig.userGuideCategories: {
      const params = new URLSearchParams({ limit: String(l), offset: String(o) });
      if (q.includes("published")) params.set("published", String(hasPublishedTrue(queries)));
      const res = await exec(`/api/v1/user-guide-categories?${params.toString()}`);
      const body = JSON.parse(res.responseBody || "{}");
      return listFromBody(body);
    }
    case appwriteConfig.videosCollectionId: {
      const params = new URLSearchParams({ limit: String(l), offset: String(o) });
      const ids = queryValues(queries, "$id");
      ids.forEach(videoId => params.append("ids", videoId));
      if (q.includes("creatorId") && (guessedId || queryValue(queries, "creatorId"))) params.set("creatorId", guessedId || queryValue(queries, "creatorId"));
      if (q.includes("category")) params.set("category", queryValue(queries, "category"));
      if (q.includes("individual")) params.set("individual", queryValue(queries, "individual"));
      if (q.includes("isActivated")) params.set("isActivated", String(/isActivated[^a-zA-Z0-9_]+true/i.test(q)));
      if (q.includes("title")) params.set("query", queryValue(queries, "title") || guessedText);
      const res = await exec(`/api/v1/videos?${params.toString()}`);
      const body = JSON.parse(res.responseBody || "{}");
      return listFromBody(body);
    }
    case appwriteConfig.videoStatsCollectionId: {
      const params = new URLSearchParams({ limit: String(l) });
      if (q.includes("videoId") && (guessedId || queryValue(queries, "videoId"))) params.set("videoId", guessedId || queryValue(queries, "videoId"));
      const res = await exec(`/api/v1/video-stats?${params.toString()}`);
      const body = JSON.parse(res.responseBody || "{}");
      return listFromBody(body);
    }
    case appwriteConfig.videoInteractionsCollectionId: {
      const params = new URLSearchParams({ limit: String(l) });
      const videoIds = queryValues(queries, "videoId");
      videoIds.forEach(videoId => params.append("videoIds", videoId));
      if (q.includes("videoId") && videoIds.length === 1) params.set("videoId", videoIds[0]);
      if (q.includes("userId")) params.set("userId", queryValue(queries, "userId") || guessedId);
      if (q.includes("type")) params.set("type", queryValue(queries, "type"));
      const res = await exec(`/api/v1/video-interactions?${params.toString()}`);
      const body = JSON.parse(res.responseBody || "{}");
      return listFromBody(body);
    }
    case appwriteConfig.videoTipsCollectionId: {
      const params = new URLSearchParams({ limit: String(l) });
      for (const field of ["videoId", "senderId", "receiverId", "status"]) {
        const value = queryValue(queries, field);
        if (value) params.set(field, value);
      }
      const res = await exec(`/api/v1/video-tips?${params.toString()}`);
      const body = JSON.parse(res.responseBody || "{}");
      return listFromBody(body);
    }
    case appwriteConfig.tipPayoutsCollectionId: {
      const params = new URLSearchParams({ limit: String(l) });
      const creatorId = queryValue(queries, "creator_id") || queryValue(queries, "creatorId") || guessedId;
      if (creatorId) params.set("creatorId", creatorId);
      const res = await exec(`/api/v1/tip-payouts?${params.toString()}`);
      const body = JSON.parse(res.responseBody || "{}");
      return listFromBody(body);
    }
    case appwriteConfig.videoReportsCollectionId: {
      const params = new URLSearchParams({ limit: String(l) });
      if (q.includes("videoId") && (guessedId || queryValue(queries, "videoId"))) params.set("videoId", guessedId || queryValue(queries, "videoId"));
      const res = await exec(`/api/v1/video-reports?${params.toString()}`);
      const body = JSON.parse(res.responseBody || "{}");
      return listFromBody(body);
    }
    case appwriteConfig.trustScoreCollectionId: { const res = await exec(guessedId ? `/api/v1/trust/${encodeURIComponent(guessedId)}` : "/api/v1/trust/me"); const body = JSON.parse(res.responseBody || "{}"); return toList(body ? [body] : [], body ? 1 : 0); }
    case appwriteConfig.trustScoreEventsCollectionId: { const res = await exec(`/api/v1/trust/${encodeURIComponent(guessedId)}/events`); const body = JSON.parse(res.responseBody || "[]"); return listFromBody(body); }
    case appwriteConfig.ratingsCollectionId: { const res = await exec(`/api/v1/trust/${encodeURIComponent(guessedId)}/ratings`); const body = JSON.parse(res.responseBody || "[]"); return listFromBody(body); }
    case appwriteConfig.referralCodesCollectionId: { const res = await exec(`/api/v1/trust/referral-codes/${encodeURIComponent(guessedId)}`); const body = JSON.parse(res.responseBody || "{}"); return toList(body ? [body] : [], body ? 1 : 0); }
    case appwriteConfig.transactionsCollectionId: { const res = await exec("/api/v1/payments/transactions"); const body = JSON.parse(res.responseBody || "{}"); return listFromBody(body); }
    case appwriteConfig.userViolationsCollectionId: {
      const res = hasQueryValue(queries, "violationStage", "banned")
        ? await exec(`/api/v1/admin/violations/banned?limit=${l}`)
        : await exec(`/api/v1/moderation/violations/${encodeURIComponent(guessedId)}`);
      const body = JSON.parse(res.responseBody || "{}");
      return hasQueryValue(queries, "violationStage", "banned") ? listFromBody(body) : toList(body ? [body] : [], body ? 1 : 0);
    }
    case appwriteConfig.kycRecordsCollectionId: { const res = await exec(`/api/v1/kyc/${encodeURIComponent(guessedId)}/status`); const body = JSON.parse(res.responseBody || "{}"); return toList(body ? [body] : [], body ? 1 : 0); }
    default: return toList([]);
  }
}

async function createDocument(collectionId: string, data: any) {
  switch (collectionId) {
    case appwriteConfig.postCollectionId: {
      const imageUrls = Array.isArray(data.imageUrl) ? data.imageUrl : [];
      const imageIds = Array.isArray(data.imageId) ? data.imageId : [];
      const media = data.media ?? imageUrls.map((url: string, index: number) => ({ fileId: imageIds[index] ?? url, url, mediaType: "application/octet-stream" }));
      return toDoc(JSON.parse((await exec("/api/v1/posts", { method: "POST", body: JSON.stringify({ individual: data.individual ?? data.category ?? data.dealType ?? "", category: data.category ?? data.dealType ?? "general", caption: data.caption ?? "", location: data.location ?? null, tags: tagsCsv(data.tags), isTrainingAvailable: !!data.isTrainingAvailable, isSellingPost: !!data.isSellingPost, isCommercialPost: data.isCommercialPost ?? true, isActivated: data.isActivated ?? false, media }) })).responseBody));
    }
    case appwriteConfig.messagesCollectionId: return toDoc(JSON.parse((await exec("/api/v1/messages", { method: "POST", body: JSON.stringify({ recipientId: data.recipientId ?? data.recipient_id, message: data.message ?? "", dealId: data.dealId ?? null, postId: data.postId ?? null, attachmentIds: data.attachmentIds ?? data.attachment_ids ?? [], attachmentMetadataJson: data.attachmentMetadataJson ?? data.attachment_metadata_json ?? null, isAgentMessage: !!(data.isAgentMessage ?? data.is_agent_message) }) })).responseBody));
    case appwriteConfig.notificationsCollectionId: return toDoc(JSON.parse((await exec("/api/v1/notifications", { method: "POST", body: JSON.stringify({ recipientId: data.recipientId ?? data.recipient_id, senderId: data.senderId ?? data.sender_id, title: data.title, body: data.body, type: data.type }) })).responseBody));
    case appwriteConfig.followsCollectionId: return toDoc(JSON.parse((await exec("/api/v1/follows", { method: "POST", body: JSON.stringify({ followingId: data.followingId, followerId: data.followerId ?? null }) })).responseBody));
    case appwriteConfig.dealStage: return toDoc(JSON.parse((await exec("/api/v1/deals", { method: "POST", body: JSON.stringify(data) })).responseBody));
    case appwriteConfig.escrowCollectionId: return toDoc(JSON.parse((await exec("/api/v1/escrows", { method: "POST", body: JSON.stringify(data) })).responseBody));
    case appwriteConfig.products: return toDoc(JSON.parse((await exec("/api/v1/live/products", { method: "POST", body: JSON.stringify({ ...data, price: numberOrDefault(data.price), minQuantity: numberOrDefault(data.minQuantity, 1), liveStreams: data.liveStreams ?? [] }) })).responseBody));
    case appwriteConfig.liveStreams: return toDoc(JSON.parse((await exec("/api/v1/live/streams", { method: "POST", body: JSON.stringify(data) })).responseBody));
    case appwriteConfig.ratingsCollectionId: return toDoc(JSON.parse((await exec("/api/v1/trust/ratings", { method: "POST", body: JSON.stringify({ dealId: data.dealId, reviewerId: data.reviewerId, recipientId: data.recipientId, rating: numberOrDefault(data.rating), comment: data.comment ?? null, userName: data.userName ?? null }) })).responseBody));
    case appwriteConfig.referralCodesCollectionId: return toDoc(JSON.parse((await exec("/api/v1/trust/referral-codes", { method: "POST", body: JSON.stringify({ userId: data.userId }) })).responseBody));
    case appwriteConfig.liveStreamsChat: return toDoc(JSON.parse((await exec("/api/v1/live/messages", { method: "POST", body: JSON.stringify({ streamId: data.streamId, userId: data.userId, userName: data.userName, userRole: data.userRole ?? null, userImageUrl: data.userImageUrl ?? null, content: data.content ?? data.text ?? "" }) })).responseBody));
    case appwriteConfig.streamPurchases: return toDoc(JSON.parse((await exec("/api/v1/live/purchases", { method: "POST", body: JSON.stringify({ ...data, quantity: numberOrDefault(data.quantity, 1) }) })).responseBody));
    case appwriteConfig.transactionsCollectionId: return toDoc(JSON.parse((await exec("/api/v1/payments/transactions", { method: "POST", body: JSON.stringify(data) })).responseBody));
    case appwriteConfig.postComments: return toDoc(JSON.parse((await exec("/api/v1/post-comments", { method: "POST", body: JSON.stringify({ postId: data.postId, userId: data.userId, userName: data.userName, text: data.text, userRole: data.userRole ?? null, timestamp: data.timestamp ?? null }) })).responseBody));
    case appwriteConfig.userGuideArtice: return toDoc(JSON.parse((await exec("/api/v1/user-guides", { method: "POST", body: JSON.stringify({ ...data, readTime: numberOrDefault(data.readTime, 5), views: numberOrDefault(data.views), tags: Array.isArray(data.tags) ? data.tags : [] }) })).responseBody));
    case appwriteConfig.userGuideCategories: return toDoc(JSON.parse((await exec("/api/v1/user-guide-categories", { method: "POST", body: JSON.stringify({ ...data, order: numberOrDefault(data.order, 1), articleCount: numberOrDefault(data.articleCount) }) })).responseBody));
    case appwriteConfig.videosCollectionId: return toDoc(JSON.parse((await exec("/api/v1/videos", { method: "POST", body: JSON.stringify({ ...data, duration: numberOrDefault(data.duration), tags: Array.isArray(data.tags) ? data.tags : [] }) })).responseBody));
    case appwriteConfig.videoStatsCollectionId: return toDoc(JSON.parse((await exec("/api/v1/video-stats", { method: "POST", body: JSON.stringify({ videoId: data.videoId, likes: numberOrDefault(data.likes), shares: numberOrDefault(data.shares), saves: numberOrDefault(data.saves), tips: numberOrDefault(data.tips), views: numberOrDefault(data.views) }) })).responseBody));
    case appwriteConfig.videoInteractionsCollectionId: return toDoc(JSON.parse((await exec("/api/v1/video-interactions", { method: "POST", body: JSON.stringify({ videoId: data.videoId, userId: data.userId, type: data.type }) })).responseBody));
    case appwriteConfig.videoTipsCollectionId: return toDoc(JSON.parse((await exec("/api/v1/video-tips", { method: "POST", body: JSON.stringify({ ...data, amount: numberOrDefault(data.amount), status: data.status ?? "pending" }) })).responseBody));
    case appwriteConfig.videoReportsCollectionId: return toDoc(JSON.parse((await exec("/api/v1/video-reports", { method: "POST", body: JSON.stringify(data) })).responseBody));
    default: return toDoc({ $id: data.$id ?? id(), id: data.id ?? data.$id ?? id(), ...data, $createdAt: now(), $updatedAt: now() });
  }
}

async function updateDocument(collectionId: string, documentId: string, data: any) {
  switch (collectionId) {
    case appwriteConfig.userCollectionId: {
      const currentUser = await getBackendCurrentUser();
      return toDoc(JSON.parse((await exec("/api/v1/users/me", {
        method: "PATCH",
        body: JSON.stringify({
          name: data.name ?? currentUser.name,
          username: data.username ?? currentUser.username,
          imageUrl: data.imageUrl ?? currentUser.imageUrl ?? null,
          bio: data.bio ?? currentUser.bio ?? "",
          acceptedSla: currentUser.acceptedSla ?? currentUser.acceptedSLA ?? false,
        }),
      })).responseBody || "{}"));
    }
    case appwriteConfig.postCollectionId:
      if (Array.isArray(data?.likedBy)) return toDoc(JSON.parse((await exec(`/api/v1/posts/${documentId}/like`, { method: data.likedBy.includes(await currentUserId()) ? "POST" : "DELETE" })).responseBody || "{}"));
      if (Array.isArray(data?.savedBy)) return toDoc(JSON.parse((await exec(`/api/v1/posts/${documentId}/save`, { method: data.savedBy.includes(await currentUserId()) ? "POST" : "DELETE" })).responseBody || "{}"));
      return toDoc(JSON.parse((await exec(`/api/v1/posts/${documentId}`, { method: "PATCH", body: JSON.stringify({ individual: data.individual ?? "", category: data.category ?? data.dealType ?? "general", caption: data.caption ?? "", location: data.location ?? null, tags: tagsCsv(data.tags), isTrainingAvailable: !!data.isTrainingAvailable, isSellingPost: !!data.isSellingPost, isCommercialPost: data.isCommercialPost ?? true, isActivated: data.isActivated ?? true, media: data.media }) })).responseBody || "{}"));
    case appwriteConfig.products:
      if (data?.isActivated === true) await exec(`/api/v1/admin/live-products/${documentId}/approve`, { method: "POST" });
      return toDoc(JSON.parse((await exec(`/api/v1/live/products/${documentId}`)).responseBody || "{}"));
    case appwriteConfig.scheduledLivesCollectionId:
      if (data?.isActivated === true) await exec(`/api/v1/admin/scheduled-lives/${documentId}/approve`, { method: "POST" });
      return toDoc(JSON.parse((await exec(`/api/v1/live/scheduled/${documentId}`)).responseBody || "{}"));
    case appwriteConfig.messagesCollectionId:
      if (data?.isRead || data?.is_read) await exec(`/api/v1/messages/${documentId}/read`, { method: "PATCH" });
      return toDoc(JSON.parse((await exec(`/api/v1/messages/${documentId}`)).responseBody || "{}"));
    case appwriteConfig.notificationsCollectionId:
      if (data?.isRead || data?.is_read) await exec(`/api/v1/notifications/${documentId}/read`, { method: "PATCH" });
      return toDoc(JSON.parse((await exec("/api/v1/notifications/me")).responseBody || "[]").find((notification: any) => (notification.id ?? notification.$id) === documentId) ?? { id: documentId });
    case appwriteConfig.dealStage: {
      const dealUpdate = {
        ...data,
        ...(data.amount !== undefined ? { amount: Number(data.amount) } : {}),
        ...(data.requestedAmount !== undefined ? { requestedAmount: Number(data.requestedAmount) } : {}),
      };
      return toDoc(JSON.parse((await exec(`/api/v1/deals/${documentId}`, { method: "PATCH", body: JSON.stringify(dealUpdate) })).responseBody || "{}"));
    }
    case appwriteConfig.escrowCollectionId: return toDoc(JSON.parse((await exec(`/api/v1/escrows/${documentId}/release`, { method: "POST", body: JSON.stringify(data) })).responseBody || "{}"));
    case appwriteConfig.liveStreams: {
      if (Array.isArray(data?.featuredProducts)) {
        return toDoc(JSON.parse((await exec(`/api/v1/live/streams/${documentId}/featured-products`, {
          method: "PATCH",
          body: JSON.stringify({ featuredProducts: data.featuredProducts }),
        })).responseBody || "{}"));
      }
      if (data?.status === "live") {
        return toDoc(JSON.parse((await exec(`/api/v1/live/streams/${documentId}/start`, { method: "POST" })).responseBody || "{}"));
      }
      if (data?.status === "ended") {
        await exec(`/api/v1/live/streams/${documentId}/end`, { method: "POST" });
      }
      if (data?.likes !== undefined) {
        await exec(`/api/v1/live/streams/${documentId}/likes`, { method: "PATCH", body: JSON.stringify(numberOrDefault(data.likes)) });
      }
      if (data?.viewerCount !== undefined || data?.viewers !== undefined) {
        await exec(`/api/v1/live/streams/${documentId}/viewer-count`, { method: "PATCH", body: JSON.stringify(numberOrDefault(data.viewerCount ?? data.viewers)) });
      }
      return toDoc(JSON.parse((await exec(`/api/v1/live/streams/${documentId}`)).responseBody || "{}"));
    }
    case appwriteConfig.postComments: return toDoc(JSON.parse((await exec(`/api/v1/post-comments/${documentId}`, { method: "PATCH", body: JSON.stringify({ text: data.text ?? data.content ?? "" }) })).responseBody || "{}"));
    case appwriteConfig.userGuideArtice: {
      const existing = toDoc(JSON.parse((await exec(`/api/v1/user-guides/${documentId}`)).responseBody || "{}"));
      const merged = { ...existing, ...data };
      return toDoc(JSON.parse((await exec(`/api/v1/user-guides/${documentId}`, { method: "PATCH", body: JSON.stringify({ ...merged, readTime: numberOrDefault(merged.readTime, 5), views: numberOrDefault(merged.views), tags: Array.isArray(merged.tags) ? merged.tags : [] }) })).responseBody || "{}"));
    }
    case appwriteConfig.userGuideCategories: return toDoc(JSON.parse((await exec(`/api/v1/user-guide-categories/${documentId}`, { method: "PATCH", body: JSON.stringify({ ...data, order: numberOrDefault(data.order, 1), articleCount: numberOrDefault(data.articleCount) }) })).responseBody || "{}"));
    case appwriteConfig.videosCollectionId: {
      const existing = toDoc(JSON.parse((await exec(`/api/v1/videos/${documentId}`)).responseBody || "{}"));
      const merged = { ...existing, ...data };
      return toDoc(JSON.parse((await exec(`/api/v1/videos/${documentId}`, { method: "PATCH", body: JSON.stringify({ ...merged, duration: numberOrDefault(merged.duration), tags: Array.isArray(merged.tags) ? merged.tags : [] }) })).responseBody || "{}"));
    }
    case appwriteConfig.videoStatsCollectionId: {
      const stats = { likes: 0, shares: 0, saves: 0, tips: 0, views: 0, videoId: data.videoId, ...data };
      return toDoc(JSON.parse((await exec(`/api/v1/video-stats/${documentId}`, { method: "PATCH", body: JSON.stringify(stats) })).responseBody || "{}"));
    }
    case appwriteConfig.videoTipsCollectionId: return toDoc(JSON.parse((await exec(`/api/v1/video-tips/${documentId}`, { method: "PATCH", body: JSON.stringify({ status: data.status ?? "pending", paymentId: data.paymentId ?? null, orderId: data.orderId ?? null }) })).responseBody || "{}"));
    default: return toDoc({ $id: documentId, id: documentId, ...data, $updatedAt: now() });
  }
}

async function deleteDocument(collectionId: string, documentId: string) {
  switch (collectionId) {
    case appwriteConfig.postCollectionId: return exec(`/api/v1/posts/${documentId}`, { method: "DELETE" });
    case appwriteConfig.messagesCollectionId: return exec(`/api/v1/messages/${documentId}`, { method: "DELETE" }).catch(() => undefined);
    case appwriteConfig.followsCollectionId: return exec(`/api/v1/follows/${documentId}`, { method: "DELETE" });
    case appwriteConfig.savesCollectionId: return exec(`/api/v1/posts/${documentId}/save`, { method: "DELETE" });
    case appwriteConfig.liveStreams: return exec(`/api/v1/live/streams/${documentId}/end`, { method: "POST" });
    case appwriteConfig.scheduledLivesCollectionId: return exec(`/api/v1/live/scheduled/${documentId}`, { method: "DELETE" });
    case appwriteConfig.userViolationsCollectionId: return exec(`/api/v1/admin/violations/${documentId}/unban`, { method: "POST" });
    case appwriteConfig.postComments: return exec(`/api/v1/post-comments/${documentId}`, { method: "DELETE" });
    case appwriteConfig.userGuideArtice: return exec(`/api/v1/user-guides/${documentId}`, { method: "DELETE" });
    case appwriteConfig.userGuideCategories: return exec(`/api/v1/user-guide-categories/${documentId}`, { method: "DELETE" });
    case appwriteConfig.videosCollectionId: return exec(`/api/v1/videos/${documentId}`, { method: "DELETE" });
    case appwriteConfig.videoStatsCollectionId: return exec(`/api/v1/video-stats/${documentId}`, { method: "DELETE" });
    case appwriteConfig.videoInteractionsCollectionId: return exec(`/api/v1/video-interactions/${documentId}`, { method: "DELETE" });
    default: return undefined;
  }
}

type RealtimeCallback = (response: any) => void;
type RealtimeConnectionCallback = () => void;
const realtimeSubscriptions = new Map<string, Set<RealtimeCallback>>();
const realtimeSubscribedChannels = new Set<string>();
const realtimeConnectionCallbacks = new Set<RealtimeConnectionCallback>();
let realtimeSocket: WebSocket | null = null;
let realtimeSocketToken = "";
let realtimeReconnectTimer: number | null = null;

function buildRealtimeUrl() {
  const url = new URL(backendBaseUrl);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = "/ws";
  const token = getStoredAccessToken();
  if (token) {
    url.searchParams.set("token", token);
  }
  return url.toString();
}

function scheduleRealtimeReconnect() {
  if (realtimeSubscribedChannels.size === 0) return;
  if (realtimeReconnectTimer !== null) return;
  realtimeReconnectTimer = window.setTimeout(() => {
    realtimeReconnectTimer = null;
    void ensureRealtimeSocket();
  }, 1500);
}

function attachRealtimeHandlers(socket: WebSocket) {
  socket.onopen = () => {
    const token = getStoredAccessToken() ?? "";
    realtimeSocketToken = token;
    for (const channel of realtimeSubscribedChannels) {
      socket.send(JSON.stringify({ type: "subscribe", channel }));
    }
    realtimeConnectionCallbacks.forEach((callback) => callback());
  };

  socket.onmessage = (event) => {
    try {
      const response = JSON.parse(event.data);
      if (response?.payload) response.payload = toDoc(response.payload);
      const channel = response?.channel;
      if (!channel) return;
      const callbacks = realtimeSubscriptions.get(channel);
      callbacks?.forEach((callback) => callback(response));
      const payloadId = response?.payload?.$id ?? response?.payload?.id;
      if (payloadId) {
        const documentCallbacks = realtimeSubscriptions.get(`${channel}.${payloadId}`);
        documentCallbacks?.forEach((callback) => callback(response));
      }
    } catch {
      // Ignore malformed realtime payloads.
    }
  };

  socket.onclose = () => {
    if (realtimeSocket === socket) {
      realtimeSocket = null;
      if (realtimeReconnectTimer !== null) {
        window.clearTimeout(realtimeReconnectTimer);
        realtimeReconnectTimer = null;
      }
      scheduleRealtimeReconnect();
    }
  };

  socket.onerror = () => {
    try {
      socket.close();
    } catch {
      // ignore
    }
  };
}

async function ensureRealtimeSocket() {
  const token = getStoredAccessToken() ?? "";
  if (realtimeSocket && realtimeSocket.readyState === WebSocket.OPEN && realtimeSocketToken === token) {
    return realtimeSocket;
  }

  if (realtimeSocket && realtimeSocket.readyState === WebSocket.CONNECTING) {
    return realtimeSocket;
  }

  if (realtimeSocket) {
    try {
      realtimeSocket.close();
    } catch {
      // ignore
    }
  }

  const socket = new WebSocket(buildRealtimeUrl());
  realtimeSocket = socket;
  attachRealtimeHandlers(socket);
  return socket;
}

function sendRealtimeCommand(command: { type: string; channel?: string }) {
  if (!realtimeSocket || realtimeSocket.readyState !== WebSocket.OPEN) return;
  realtimeSocket.send(JSON.stringify(command));
}

export const client: any = {
  setEndpoint: (..._args: any[]) => client,
  setProject: (..._args: any[]) => client,
  subscribe: (channel: string, callback: RealtimeCallback) => {
    if (!realtimeSubscriptions.has(channel)) {
      realtimeSubscriptions.set(channel, new Set());
    }
    realtimeSubscriptions.get(channel)!.add(callback);
    realtimeSubscribedChannels.add(channel);

    void ensureRealtimeSocket().then((socket) => {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: "subscribe", channel }));
      }
    });

    const unsubscribe = () => {
      const callbacks = realtimeSubscriptions.get(channel);
      callbacks?.delete(callback);
      if (!callbacks || callbacks.size === 0) {
        realtimeSubscriptions.delete(channel);
        realtimeSubscribedChannels.delete(channel);
        sendRealtimeCommand({ type: "unsubscribe", channel });
      }
    };

    return Object.assign(unsubscribe, { unsubscribe });
  },
  unsubscribe: async (channel: string) => {
    realtimeSubscriptions.delete(channel);
    realtimeSubscribedChannels.delete(channel);
    sendRealtimeCommand({ type: "unsubscribe", channel });
  },
  onRealtimeConnected: (callback: RealtimeConnectionCallback) => {
    realtimeConnectionCallbacks.add(callback);
    return () => realtimeConnectionCallbacks.delete(callback);
  },
};

export const account: any = {
  createEmailPasswordSession: async (...args: any[]) => ({ $id: id(), ...(await signInBackendUser(authEmail(args[0]), args[1])) }),
  get: async () => getBackendCurrentUser(),
  deleteSession: async (..._args: any[]) => (clearBackendSession(), {}),
  listSessions: async () => ({ sessions: [] }),
  createOAuth2Session: async (provider: string, _successUrl?: string, _failureUrl?: string, options?: any) => {
    const token = typeof options === "string" ? options : options?.token ?? options?.idToken ?? options?.credential;
    if (!token) {
      return { success: false, provider, message: "OAuth sign-in now expects an id token from Google or Apple." };
    }

    return backendRequest("/api/v1/auth/oauth", {
      method: "POST",
      body: JSON.stringify({
        provider,
        token,
        deviceName: options?.deviceName ?? navigator.userAgent.slice(0, 120),
        email: options?.email ?? null,
        displayName: options?.displayName ?? null,
      }),
    });
  },
  createSession: async (..._args: any[]) => ({ $id: id() }),
  createPhoneToken: async (..._args: any[]) => ({ $id: id() }),
  updateEmail: async (..._args: any[]) => ({ $id: id() }),
  updateName: async (..._args: any[]) => ({ $id: id() }),
};

export const databases: any = {
  client,
  listDocuments: async (_db: string, collectionId: string, queries?: string[]) => listDocuments(collectionId, queries),
  getDocument: async (_db: string, collectionId: string, documentId: string) => {
    switch (collectionId) {
      case appwriteConfig.postCollectionId: return toDoc(JSON.parse((await exec(`/api/v1/posts/${documentId}`)).responseBody || "{}"));
      case appwriteConfig.userCollectionId: return toDoc(await getBackendUserById(documentId));
      case appwriteConfig.messagesCollectionId: return toDoc(JSON.parse((await exec(`/api/v1/messages/${documentId}`)).responseBody || "{}"));
      case appwriteConfig.dealStage: return toDoc(JSON.parse((await exec(`/api/v1/deals/${documentId}`)).responseBody || "{}"));
      case appwriteConfig.escrowCollectionId: return toDoc(JSON.parse((await exec(`/api/v1/escrows/${documentId}`)).responseBody || "{}"));
      case appwriteConfig.liveStreams: return toDoc(JSON.parse((await exec(`/api/v1/live/streams/${documentId}`)).responseBody || "{}"));
      case appwriteConfig.products: return toDoc(JSON.parse((await exec(`/api/v1/live/products/${documentId}`)).responseBody || "{}"));
      case appwriteConfig.postComments: return toDoc(JSON.parse((await exec(`/api/v1/post-comments/${documentId}`)).responseBody || "{}"));
      case appwriteConfig.userGuideArtice: return toDoc(JSON.parse((await exec(`/api/v1/user-guides/${documentId}`)).responseBody || "{}"));
      case appwriteConfig.userGuideCategories: return toDoc(JSON.parse((await exec(`/api/v1/user-guide-categories/${documentId}`)).responseBody || "{}"));
      case appwriteConfig.videosCollectionId: return toDoc(JSON.parse((await exec(`/api/v1/videos/${documentId}`)).responseBody || "{}"));
      default: return toDoc({ $id: documentId, id: documentId });
    }
  },
  createDocument: async (_db: string, collectionId: string, documentId: string, data: any) => toDoc(await createDocument(collectionId, { $id: documentId, id: documentId, ...data })),
  updateDocument: async (_db: string, collectionId: string, documentId: string, data: any) => toDoc(await updateDocument(collectionId, documentId, data)),
  deleteDocument: async (_db: string, collectionId: string, documentId: string) => deleteDocument(collectionId, documentId),
};

export const storage: any = {
  createFile: async (_bucket: string, fileId: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const uploaded = await backendRequest<any>("/api/v1/media/upload", { method: "POST", body: formData });
    const resolvedId = uploaded?.fileId ?? uploaded?.FileId ?? fileId ?? id();
    const url = normalizeMediaUrl(uploaded?.url ?? uploaded?.Url ?? resolvedId ?? "");
    if (resolvedId && url) fileUrlCache.set(resolvedId, url);
    return toDoc({ $id: resolvedId, id: resolvedId, url, fileId: resolvedId, size: uploaded?.size ?? uploaded?.Size ?? file.size, contentType: uploaded?.contentType ?? uploaded?.ContentType ?? file.type });
  },
  getFilePreview: (_bucket: string, fileId: string) => fileUrlCache.get(fileId) ?? normalizeMediaUrl(fileId),
  getFileView: (_bucket: string, fileId: string) => fileUrlCache.get(fileId) ?? normalizeMediaUrl(fileId),
  getFileDownload: (_bucket: string, fileId: string) => fileUrlCache.get(fileId) ?? normalizeMediaUrl(fileId),
  deleteFile: async (_bucket: string, fileId: string) => (fileUrlCache.delete(fileId), exec(`/api/v1/media/${encodeURIComponent(fileId)}`, { method: "DELETE" })),
};

export const avatars: any = { getInitials: (name: string) => `https://ui-avatars.com/api/?name=${encodeURIComponent(name || "User")}&background=2563eb&color=ffffff` };
export const functions: any = { createExecution: async (...args: any[]) => fn(args[0], args[1]) };
export const getFilePreview = (fileId: any) => storage.getFilePreview(appwriteConfig.storageId, fileId);
export const getFileView = (fileId: any) => storage.getFileView(appwriteConfig.storageId, fileId);
export const getFileDownload = (fileId: any) => storage.getFileDownload(appwriteConfig.storageId, fileId);
